import { ChangeEvent, ReactElement, useRef } from 'react';
import { getBase64 } from '../_helpers/globalHelper';
import Button from './Button';

/** A picked image: a data URL for instant preview and the bytes to upload. */
export type PickedDeviceImage = {
  dataUrl: string;
  blob: Blob | null;
};

type DeviceImageUploadProps = {
  label?: string;
  value: string | null;
  onChange: (image: PickedDeviceImage | null) => void;
};

// The photo is stored on the server now rather than squeezed into
// localStorage, so it is downscaled for bandwidth and consistency rather than
// to fit a 5MB browser quota — big enough to be a useful reference shot.
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

/**
 * Downscales an image file, returning both a preview data URL and the encoded
 * bytes to upload. Falls back to the original encoding (and no blob, so the
 * caller can still preview) if the canvas pipeline is unavailable.
 * @param file - Selected image file.
 * @returns - The downscaled preview and upload payload.
 */
async function prepareImage(file: File): Promise<PickedDeviceImage> {
  const original = (await getBase64(file)) as string;
  return new Promise((resolve) => {
    const img = new Image();
    /**
     * Draws the loaded image onto a scaled canvas and resolves with its data URL and blob.
     */
    img.onload = (): void => {
      const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
      const width = Math.max(1, Math.round(img.width * scale));
      const height = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve({ dataUrl: original, blob: null });
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      canvas.toBlob((blob) => resolve({ dataUrl, blob }), 'image/jpeg', JPEG_QUALITY);
    };
    /**
     * Falls back to the original encoding if the image cannot be decoded.
     */
    img.onerror = (): void => {
      resolve({ dataUrl: original, blob: null });
    };
    img.src = original;
  });
}

/**
 * Image upload control with preview, replace and remove actions.
 * @param props - DeviceImageUploadProps.
 * @returns - A React element for uploading a device image.
 */
export default function DeviceImageUpload(props: DeviceImageUploadProps): ReactElement {
  const { label = 'Device Image', value, onChange } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Reads the selected file, downscales it and reports the data URL.
   * @param event - The file input change event.
   */
  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    // Reset so re-selecting the same file fires another change event.
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (!file || !file.type.startsWith('image/')) {
      return;
    }
    onChange(await prepareImage(file));
  };

  return (
    <>
      <div className="pt-3 mb-2 flex text-gray-500 dark:text-gray-400 text-sm">{label}</div>
      <div className="border border-gray-700 rounded-lg p-3 flex items-center gap-3">
        {value ? (
          <img
            src={value}
            alt="Device"
            className="h-12 w-12 rounded object-cover border border-gray-700 flex-shrink-0"
          />
        ) : (
          <div className="h-12 w-12 rounded bg-gray-100 dark:bg-container-500 border border-gray-700 flex items-center justify-center text-gray-400 flex-shrink-0">
            <span className="material-icons-outlined text-xl">image</span>
          </div>
        )}
        <span className="flex-1 text-base font-normal text-gray-500 dark:text-gray-300 truncate">
          {value ? 'Image selected' : 'No image selected'}
        </span>
        <input
          type="file"
          accept="image/*"
          className="hidden"
          ref={inputRef}
          onChange={handleFileChange}
        />
        <div className="flex gap-2 flex-shrink-0">
          <Button
            theme="secondary-color"
            size="xs"
            name={value ? 'Replace' : 'Upload'}
            onClick={() => inputRef.current?.click()}
          />
          {value && (
            <Button theme="secondary-gray" size="xs" name="Remove" onClick={() => onChange(null)} />
          )}
        </div>
      </div>
      <div className="w-full pt-2 pb-2 text-sm font-normal leading-tight text-gray-500">
        Optional. A square JPG or PNG works best; large images are resized automatically and shared
        with everyone on your account.
      </div>
    </>
  );
}
